import type { FormEvent } from "react";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { signInWithPasswordSession } from "@/features/auth/api/password-auth";
import {
  completeStaffPasswordReset,
  sendStaffPasswordReset,
} from "@/features/auth/api/reset-password";
import {
  StaffAuthClientError,
  sendStaffSmsCode,
  staffAuthSessionToResult,
  verifyStaffSmsCode,
} from "@/features/auth/api/staff-auth-client";
import type {
  AuthMode,
  LoginFormsProps,
  ResetStep,
  SmsStep,
} from "@/features/auth/components/LoginForms";
import type { StaffSessionResult } from "@/features/auth/types";
import { StaffSessionError } from "../approval";
import {
  type CompletedLoginMethod,
  type LoginMethod,
  trackLoginCompleted,
  trackLoginError,
} from "../login-observability";

const DEFAULT_SIGN_IN_ERROR = "We couldn't sign you in. Check your email and password.";
const GENERIC_SMS_ERROR = "We couldn't verify that code. Try again.";
const DEFAULT_RESET_REQUEST_ERROR = "We couldn't send a reset code. Check the email and try again.";
const DEFAULT_RESET_ERROR = "We couldn't reset your password. Check the code and try again.";

interface UseLoginFormControllerOptions {
  completeSignIn: (result: StaffSessionResult) => boolean;
  isAuthLoading: boolean;
  initialAuthMode?: AuthMode;
  navigate: NavigateFunction;
  redirectTo: string;
}

/** Reads a text field from submitted form data as a string, never a File. */
function readFormField(formData: FormData, name: string): string {
  const value = formData.get(name);
  return typeof value === "string" ? value : "";
}

function getStaffAuthErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof StaffAuthClientError) {
    return error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

function finishLogin(input: {
  readonly completeSignIn: (result: StaffSessionResult) => boolean;
  readonly method: CompletedLoginMethod;
  readonly navigate: NavigateFunction;
  readonly redirectTo: string;
  readonly result: StaffSessionResult;
}): void {
  if (!input.completeSignIn(input.result)) {
    throw new StaffSessionError("session_invalid", "Your secure session expired. Sign in again.");
  }
  trackLoginCompleted(input.method);
  void input.navigate(input.redirectTo, { replace: true });
}

/** Owns the password, SMS, and reset form state returned to LoginForms. */
export function useLoginFormController({
  completeSignIn,
  isAuthLoading,
  initialAuthMode = "password",
  navigate,
  redirectTo,
}: UseLoginFormControllerOptions) {
  const [formError, setFormError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>(initialAuthMode);
  const [smsStep, setSmsStep] = useState<SmsStep>("phone");
  const [smsPhone, setSmsPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetStep, setResetStep] = useState<ResetStep>("request");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCompleteEmail, setResetCompleteEmail] = useState<string | null>(null);

  async function runFormSubmit(input: {
    readonly event: FormEvent<HTMLFormElement>;
    readonly method: LoginMethod;
    readonly fallbackError: string;
    readonly action: (formData: FormData) => Promise<void>;
  }): Promise<void> {
    input.event.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    const formData = new FormData(input.event.currentTarget);

    try {
      await input.action(formData);
    } catch (error) {
      trackLoginError(input.method, error);
      setFormError(getStaffAuthErrorMessage(error, input.fallbackError));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    await runFormSubmit({
      event,
      method: "password",
      fallbackError: DEFAULT_SIGN_IN_ERROR,
      action: async (formData) => {
        const email = readFormField(formData, "email").trim();
        const password = readFormField(formData, "password");
        const result = await signInWithPasswordSession({ email, password });
        finishLogin({
          completeSignIn,
          method: "password",
          navigate,
          redirectTo,
          result,
        });
      },
    });
  }

  async function requestSmsCode(phone: string) {
    await sendStaffSmsCode(phone);
    setSmsPhone(phone);
    setOtpSent(true);
    setSmsStep("code");
  }

  async function verifySmsCode(phone: string, code: string) {
    const response = await verifyStaffSmsCode({ code, phone });
    finishLogin({
      completeSignIn,
      method: "sms_otp",
      navigate,
      redirectTo,
      result: staffAuthSessionToResult(response),
    });
  }

  async function handleSmsSubmit(event: FormEvent<HTMLFormElement>) {
    await runFormSubmit({
      event,
      method: "sms_otp",
      fallbackError: GENERIC_SMS_ERROR,
      action: async (formData) => {
        const phone = (readFormField(formData, "phone") || smsPhone).trim();
        const code = readFormField(formData, "code").trim();
        if (smsStep === "phone") {
          await requestSmsCode(phone);
          return;
        }
        await verifySmsCode(phone, code);
      },
    });
  }

  async function handleResetSubmit(event: FormEvent<HTMLFormElement>) {
    const useExistingCode =
      (event.nativeEvent as SubmitEvent | undefined)?.submitter?.getAttribute("value") ===
      "existing-code";
    await runFormSubmit({
      event,
      method: "password_reset",
      fallbackError: resetStep === "request" ? DEFAULT_RESET_REQUEST_ERROR : DEFAULT_RESET_ERROR,
      action: async (formData) => {
        const email = (readFormField(formData, "email") || resetEmail).trim();
        if (resetStep === "request") {
          if (!useExistingCode) await sendStaffPasswordReset(email);
          setResetEmail(email);
          setResetStep("code");
          return;
        }
        const code = readFormField(formData, "code").trim();
        const newPassword = readFormField(formData, "password");
        await completeStaffPasswordReset({ code, email, newPassword });
        setResetCompleteEmail(email);
      },
    });
  }

  function switchAuthMode(mode: AuthMode) {
    setAuthMode(mode);
    setFormError(null);
    setResetCompleteEmail(null);
    setIsSubmitting(false);
    if (mode === "sms") {
      setSmsStep(otpSent ? "code" : "phone");
    }
    if (mode === "reset") {
      setResetStep("request");
      setResetEmail("");
    }
  }

  function resetPasswordReset() {
    setResetCompleteEmail(null);
    setResetStep("request");
    setResetEmail("");
    setAuthMode("password");
    setFormError(null);
  }

  function resetSmsPhone() {
    setSmsStep("phone");
    setOtpSent(false);
  }

  return {
    authMode,
    formError,
    isFormDisabled: isSubmitting || isAuthLoading,
    isSubmitting,
    onAuthModeChange: switchAuthMode,
    onPasswordSubmit: handleSubmit,
    onResetReset: resetPasswordReset,
    onResetSubmit: handleResetSubmit,
    onSmsPhoneChange: setSmsPhone,
    onSmsSubmit: handleSmsSubmit,
    onUseAnotherNumber: resetSmsPhone,
    resetCompleteEmail,
    resetEmail,
    resetStep,
    smsPhone,
    smsStep,
  } satisfies LoginFormsProps;
}
